import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Overview from './pages/Overview';
import DevelopmentDashboard from './pages/development/DevelopmentDashboard';
import DevelopmentIterations from './pages/development/DevelopmentIterations';
import DevelopmentDevelopers from './pages/development/DevelopmentDevelopers';
import Finance from './pages/Finance';
import Talent from './pages/Talent';
import Settings from './pages/Settings';
import Support from './pages/Support';
import SupportOverview from './pages/support/SupportOverview';
import SupportCustomers from './pages/support/SupportCustomers';
import SupportTeamMembers from './pages/support/SupportTeamMembers';
import MarketInsights from './pages/MarketInsights';
import Login from './pages/Login';
import Register from './pages/Register';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ProtectedRoute from './components/ProtectedRoute';
import DevelopmentDataGuard from './components/DevelopmentDataGuard';
import Navbar from './components/Navbar';
import { AuthProvider } from './context/AuthContext';
import { DevelopmentDataProvider } from './context/DevelopmentDataContext';
import { ProjectSelectionProvider } from './context/ProjectSelectionContext';
import { SupportInstanceSelectionProvider } from './context/SupportInstanceSelectionContext';
import Clarity from '@microsoft/clarity';


const clarityProjectId = "stkwji5mqs"
const GA_MEASUREMENT_ID = "G-4YCB915L6X"; // Replace with your actual GA4 Measurement ID

if (process.env.NODE_ENV === 'production') {
  Clarity.init(clarityProjectId);

  // Initialize Google Analytics
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA_MEASUREMENT_ID}');
  `;
  document.head.appendChild(script2);
}

// Component to get current path for DevelopmentDataGuard
const CurrentPathProvider: React.FC = () => {
  const location = useLocation();
  return (
    <DevelopmentDataGuard
      redirectTo="/development/overview"
      allowDashboard={true}
      currentPath={location.pathname}
    />
  );
};

// Responsive layout component
const ResponsiveLayout: React.FC = () => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Navbar />
      <main style={{
        flex: 1,
        background: '#f4f6fa',
        marginLeft: isMobile ? '0' : '220px',
        paddingTop: isMobile ? '80px' : '0' // Add top padding for mobile hamburger button
      }}>
        <Outlet />
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<ResponsiveLayout />}>
              <Route index element={<Navigate to="/overview" replace />} />
              <Route path="/overview" element={<Overview />} />

              {/* Development routes with data guard */}
              {/* Development routes are wrapped in their own data provider */}
              <Route path="/development/*" element={
                <DevelopmentDataProvider>
                  <ProjectSelectionProvider>
                    <Routes>
                      <Route path="/overview" element={<DevelopmentDashboard />} />
                      <Route element={<CurrentPathProvider />}>
                        <Route path="/iterations" element={<DevelopmentIterations />} />
                        <Route path="/developers" element={<DevelopmentDevelopers />} />
                      </Route>
                    </Routes>
                  </ProjectSelectionProvider>
                </DevelopmentDataProvider>
              } />

              <Route path="/finance" element={<Finance />} />
              <Route path="/talent" element={<Talent />} />
              <Route path="/support/*" element={
                <SupportInstanceSelectionProvider>
                  <Routes>
                    <Route path="/overview" element={<SupportOverview />} />
                    <Route path="/customers" element={<SupportCustomers />} />
                    <Route path="/team-members" element={<SupportTeamMembers />} />
                  </Routes>
                </SupportInstanceSelectionProvider>
              } />
              <Route path="/market-insights" element={<MarketInsights />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
